import { inflect } from 'inflection';
import * as _ from 'lodash-es';
import React, { useMemo } from 'react';
import getRemoveTopicDisabledMessage from './getRemoveTopicDisabledMessage';
import {
  useRemoveGroupFromPost,
  useRemovePostUser,
  useRemoveTopicFromPost,
  useUpdatePostGroup,
  useUpdatePostLinkAccess,
  useUpdatePostUser,
} from './postPermissionOperations';
import SharingDropdownAccessLevelSelect from './SharingDropdownAccessLevelSelect';
import * as styles from './SharingDropdownAccessList.module.scss';
import SharingDropdownRow from './SharingDropdownRow';
import useAppSelector from '../../app/redux/useAppSelector';
import BorderedIcon from '../../components/BorderedIcon';
import CallOut from '../../components/CallOut';
import Tooltip from '../../components/Tooltip';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { PostLinkAccess, PostShareRole } from '../../graphql/generated/graphql';
import GroupIcon from '../../groups/GroupIcon';
import { useAllGroupMap } from '../../groups/useAllGroups';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import publicIcon from '../../icons/non-prefilled/16x16/public.svg';
import TeamIcon from '../../organization/TeamIcon';
import TopicIcon from '../../topic/TopicIcon';
import TopicNameWithIcon from '../../topic/TopicNameWithIcon';
import topicPolicyCheck, {
  TopicPolicyAction,
  TopicRole,
  UserRole,
} from '../../topic/topicPolicyCheck';
import { useAllTopicMap } from '../../topic/useAllTopics';
import getTopicUsers from '../../topic/utils/getTopicUsers';
import { useAllUsersMap, useGetUser } from '../../user/useAllUsers';
import UserRow from '../../user/UserRow';
import { nonEmptyId } from '../../utils/nonEmpty';
import populateObjects from '../../utils/populateObjects';
import sortTopicsByName from '../../utils/sortTopicsByName';
import {
  isAllowedThroughLinkAccess,
  isAllowedThroughTopicSharing,
  PostPolicyAction,
  usePostPolicyCheck,
} from '../postPolicyCheck';

const SHARING_DROPDOWN_POST_FRAGMENT = gql(`
  fragment SharingDropdownAccessList_post on Post {
    id
    publishedAt
    owner {
      ...useGetUser_user
    }
    topics {
      id
    }
    sharing {
      editorIds
      viewerIds
      editorGroupIds
      viewerGroupIds
    }
    ...usePostPolicyCheck_post
    ...getRemoveTopicDisabledMessage_post
    ...isAllowedThroughLinkAccess_post
  }
`);

const TOPIC_ROLE_DISABLE_MESSAGE =
  "Access granted by topic's settings. Please change the topic's settings to modify edit/view access.";
const LINK_ROLE_TOOLTIP = 'Access set by\ntopics this post\nis organized in';

interface SharingDropdownProps {
  post: DocumentType<typeof SHARING_DROPDOWN_POST_FRAGMENT>;
}

const SharingDropdownAccessList = ({
  post,
  post: { publishedAt, owner, sharing },
}: SharingDropdownProps) => {
  const { editorIds, viewerIds, editorGroupIds, viewerGroupIds } =
    sharing || {};

  const allTopicMap = useAllTopicMap();
  const getUser = useGetUser();
  const teamName = useAppSelector((state) => state.session.organization.name);
  const currentUser = useAppSelector((state) => state.session.user);
  const postPolicyCheck = usePostPolicyCheck();

  const postWithFullTopics = useMemo(
    () => ({
      ...post,
      topics: _.compact(post.topics.map(({ id }) => allTopicMap[id])),
    }),
    [post, allTopicMap],
  );

  const canEditUsersAndLink = postPolicyCheck(
    PostPolicyAction.EditPost,
    post,
    currentUser,
  );

  const removeTopicMutation = useRemoveTopicFromPost();
  const removeTopic = _.partial(removeTopicMutation, post);

  const updateLinkAccess = useUpdatePostLinkAccess();
  const removeUser = useRemovePostUser();
  const updateUser = useUpdatePostUser();

  const removeGroup = useRemoveGroupFromPost();
  const updateGroup = useUpdatePostGroup();

  const isPublicThroughLink = isAllowedThroughLinkAccess(
    PostPolicyAction.ViewPost,
    post,
    UserRole.Anonymous,
  );
  const isPublicThroughTopics = isAllowedThroughTopicSharing(
    PostPolicyAction.ViewPost,
    postWithFullTopics,
    UserRole.Anonymous,
  );
  const isPublic = isPublicThroughLink || isPublicThroughTopics;
  const canFullUserView = postPolicyCheck(
    PostPolicyAction.ViewPost,
    post,
    UserRole.Full,
  );
  const canFullUserEdit = postPolicyCheck(
    PostPolicyAction.EditPost,
    post,
    UserRole.Full,
  );
  const canPublicEdit = postPolicyCheck(
    PostPolicyAction.EditPost,
    post,
    UserRole.Anonymous,
  );

  const topics = useMemo(
    () =>
      sortTopicsByName(postWithFullTopics.topics).map((topic) => {
        const canMembersEdit =
          canFullUserEdit ||
          topicPolicyCheck(TopicPolicyAction.EditPost, topic, TopicRole.Member);

        const { members, owners } = getTopicUsers(topic);
        const memberCount = members.length;
        const ownerCount = owners.length;
        const ownerString = ownerCount
          ? `${ownerCount} ${inflect('owner', ownerCount)}`
          : null;
        const memberString = memberCount
          ? `${memberCount} ${inflect('member', memberCount)}`
          : null;

        const combinedString = _.compact([ownerString, memberString]).join(
          ' and ',
        );

        return {
          canMembersEdit,
          memberString,
          ownerString,
          combinedString,
          topic,
        };
      }),
    [canFullUserEdit, postWithFullTopics.topics],
  );

  const allUsersMap = useAllUsersMap();
  const editorUsers = useMemo(
    () => populateObjects(allUsersMap, editorIds).filter(nonEmptyId),
    [allUsersMap, editorIds],
  );
  const viewerUsers = useMemo(
    () => populateObjects(allUsersMap, viewerIds).filter(nonEmptyId),
    [allUsersMap, viewerIds],
  );

  const allGroupsMap = useAllGroupMap();
  const editorGroups = useMemo(
    () => populateObjects(allGroupsMap, editorGroupIds).filter(nonEmptyId),
    [allGroupsMap, editorGroupIds],
  );
  const viewerGroups = useMemo(
    () => populateObjects(allGroupsMap, viewerGroupIds).filter(nonEmptyId),
    [allGroupsMap, viewerGroupIds],
  );

  return (
    <div>
      {!publishedAt && topics.length > 0 && (
        <div className={styles.row}>
          <CallOut variant="warning">
            Topic access will take effect after publishing
          </CallOut>
        </div>
      )}
      {owner && (
        <SharingDropdownRow
          info={<UserRow showBadge user={getUser(owner)} />}
          right="Owner"
        />
      )}
      {editorUsers.map((user) => (
        <SharingDropdownRow
          key={user.id}
          info={<UserRow user={user} showBadge />}
          right={
            canEditUsersAndLink ? (
              <SharingDropdownAccessLevelSelect
                value="EDITOR"
                onRemove={() => removeUser(post, user.id)}
                onChange={(value) =>
                  updateUser(
                    post,
                    user.id,
                    value === 'EDITOR'
                      ? PostShareRole.Editor
                      : PostShareRole.Viewer,
                  )
                }
              />
            ) : (
              'Editor'
            )
          }
        />
      ))}
      {viewerUsers.map((user) => (
        <SharingDropdownRow
          key={user.id}
          info={<UserRow user={user} showBadge />}
          right={
            canEditUsersAndLink ? (
              <SharingDropdownAccessLevelSelect
                value="VIEWER"
                onRemove={() => removeUser(post, user.id)}
                onChange={(value) =>
                  updateUser(
                    post,
                    user.id,
                    value === 'EDITOR'
                      ? PostShareRole.Editor
                      : PostShareRole.Viewer,
                  )
                }
              />
            ) : (
              'Viewer'
            )
          }
        />
      ))}
      {editorGroups.map((group) => (
        <SharingDropdownRow
          key={group.id}
          icon={<GroupIcon />}
          title={group.name}
          description={`${group.allUsers.length} members`}
          right={
            canEditUsersAndLink ? (
              <SharingDropdownAccessLevelSelect
                value="EDITOR"
                onRemove={() => removeGroup(post, group)}
                onChange={(value) =>
                  updateGroup(
                    post,
                    group,
                    value === 'EDITOR'
                      ? PostShareRole.Editor
                      : PostShareRole.Viewer,
                  )
                }
              />
            ) : (
              'Editor'
            )
          }
        />
      ))}
      {viewerGroups.map((group) => (
        <SharingDropdownRow
          key={group.id}
          icon={<GroupIcon />}
          title={group.name}
          description={`${group.allUsers.length} members`}
          right={
            canEditUsersAndLink ? (
              <SharingDropdownAccessLevelSelect
                value="VIEWER"
                onRemove={() => removeGroup(post, group)}
                onChange={(value) =>
                  updateGroup(
                    post,
                    group,
                    value === 'EDITOR'
                      ? PostShareRole.Editor
                      : PostShareRole.Viewer,
                  )
                }
              />
            ) : (
              'Viewer'
            )
          }
        />
      ))}
      {topics.map(({ topic, canMembersEdit, ownerString, combinedString }) => {
        const userString = canMembersEdit ? combinedString : ownerString;

        return (
          !!userString && (
            <SharingDropdownRow
              key={topic.id}
              icon={<TopicIcon />}
              title={<TopicNameWithIcon topic={topic} />}
              description={userString}
              right={
                <SharingDropdownAccessLevelSelect
                  changeDisabledMessage={TOPIC_ROLE_DISABLE_MESSAGE}
                  removeDisabledMessage={getRemoveTopicDisabledMessage(
                    topic,
                    currentUser,
                    post,
                  )}
                  value="EDITOR"
                  onRemove={() => removeTopic(topic)}
                />
              }
            />
          )
        );
      })}
      {topics
        .filter((t) => !t.canMembersEdit && t.memberString)
        .map(({ topic, memberString }) => (
          <SharingDropdownRow
            key={topic.id}
            icon={<TopicIcon />}
            title={<TopicNameWithIcon topic={topic} />}
            description={memberString}
            right={
              <SharingDropdownAccessLevelSelect
                changeDisabledMessage={TOPIC_ROLE_DISABLE_MESSAGE}
                removeDisabledMessage={getRemoveTopicDisabledMessage(
                  topic,
                  currentUser,
                  post,
                )}
                value="VIEWER"
                onRemove={() => removeTopic(topic)}
              />
            }
          />
        ))}
      {isPublic && (
        <SharingDropdownRow
          icon={<BorderedIcon>{publicIcon}</BorderedIcon>}
          title="Public"
          description="Everyone on the internet can access"
          right={
            !canEditUsersAndLink ? (
              <Tooltip content={LINK_ROLE_TOOLTIP} placement="bottom">
                {canPublicEdit ? 'Editor' : 'Viewer'}
              </Tooltip>
            ) : (
              <SharingDropdownAccessLevelSelect
                value={canPublicEdit ? 'EDITOR' : 'VIEWER'}
                onChange={(level) =>
                  updateLinkAccess(
                    post,
                    level === 'EDITOR'
                      ? PostLinkAccess.PublicEdit
                      : PostLinkAccess.Public,
                  )
                }
                onRemove={
                  isPublicThroughTopics
                    ? undefined
                    : () => updateLinkAccess(post, PostLinkAccess.Internal)
                }
              />
            )
          }
        />
      )}
      {!isPublic &&
        canFullUserView &&
        (publishedAt ? (
          <SharingDropdownRow
            icon={<TeamIcon size="medium" />}
            title={teamName}
            description="Any standard user can access"
            right={
              <Tooltip content={LINK_ROLE_TOOLTIP} placement="bottom">
                {canFullUserEdit ? 'Editor' : 'Viewer'}
              </Tooltip>
            }
          />
        ) : (
          <SharingDropdownRow
            icon={<BorderedIcon>{linkIcon}</BorderedIcon>}
            title="Direct"
            description="Teammates with this link can edit"
            right={
              canEditUsersAndLink ? (
                <SharingDropdownAccessLevelSelect
                  changeDisabledMessage="Editor access granted by “Link access” settings below. Change to “Disabled” to specify access for individual users."
                  value="EDITOR"
                  onRemove={() =>
                    updateLinkAccess(post, PostLinkAccess.Disabled)
                  }
                />
              ) : (
                'Editor'
              )
            }
          />
        ))}
    </div>
  );
};

export default SharingDropdownAccessList;
